class BasicHeader extends HTMLElement {
  constructor() {
    super();
  }

  get headerSection() {
    return document.querySelector(".f-section-header");
  }

  connectedCallback() {
    this.classes = {
      active: "f-header__mega-active",
      headerScheme: this.dataset.headerColorScheme,
      dropdownScheme: this.dataset.dropdownColorScheme,
    };

    this.grandLinks = this.querySelectorAll(".f-site-nav__sub-item--has-child");
    this.grandLinks &&
      this.grandLinks.forEach((item) => {
        this.handleGrandLinksPosition(item);
      });
  }

  handleMegaItemActive(dropdown) {
    // Calculate backdrop height.
    const rect = dropdown.getBoundingClientRect();
    this.style.setProperty(
      "--f-dropdown-height",
      Math.ceil(rect.height) + "px"
    );

    this.classList.remove(this.classes.headerScheme);
    this.classList.add(this.classes.active, this.classes.dropdownScheme);
    document.documentElement.style.setProperty(
      "--f-header-height",
      this.clientHeight + "px"
    );
  }

  handleMegaItemDeactive() {
    this.classList.remove(this.classes.active, this.classes.dropdownScheme);
    this.classList.add(this.classes.headerScheme);
    document.documentElement.style.setProperty(
      "--f-header-height",
      this.clientHeight + "px"
    );
  }

  handleGrandLinksPosition(target) {
    const dropdownLV3 = target.querySelector(".f-site-nav__dropdown");
    if (dropdownLV3) {
      const rect = dropdownLV3.getBoundingClientRect();
      dropdownLV3.classList.remove("f-site-nav__dropdown-reversed");
      if (
        (!FoxThemeSettings.isRTL &&
          document.documentElement.clientWidth < rect.x + rect.width + 10) ||
        (FoxThemeSettings.isRTL && rect.x < 10)
      ) {
        dropdownLV3.classList.add("f-site-nav__dropdown-reversed");
      }
    }
  }
}
customElements.define("basic-header", BasicHeader, { extends: "header" });

class StickyHeader extends BasicHeader {
  constructor() {
    super();

    this.stickyClasses = {
      pinned: "header-pinned",
    };
  }

  connectedCallback() {
    super.connectedCallback();

    this.headerSection.classList.add("header-sticky");
    this.stickyType = this.dataset.stickyType;
    this.currentScrollTop = 0;

    this.headerBounds = this.headerSection
      .querySelector(".header")
      .getBoundingClientRect();

    this.onScrollHandler = this._onScroll.bind(this);
    this._onScroll();
    window.addEventListener("scroll", this.onScrollHandler, false);
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.onScrollHandler);
  }

  get isAlwaysSticky() {
    return this.stickyType === "always";
  }

  _onScroll() {
    const scrollTop = window.scrollY;
    const headerSection = this.headerSection;

    // Avoid layout thrashing by caching values
    const headerBoundsTop = this.offsetTop + this.headerBounds.height;

    const headerBoundsBottom =
      this.headerBounds.top + this.headerBounds.height + 100;

    // Utilize batch CSS classes and updates
    requestAnimationFrame(() => {
      if (scrollTop > headerBoundsTop) {
        headerSection.classList.add("header-scrolled");

        if (this.isAlwaysSticky) {
          document.body.classList.add(this.stickyClasses.pinned);
        } else {
          // Sticky on scroll up.
          if (
            scrollTop < this.currentScrollTop ||
            scrollTop < headerBoundsBottom
          ) {
            document.body.classList.add(this.stickyClasses.pinned);
          } else {
            document.body.classList.remove(this.stickyClasses.pinned);
          }
        }
      } else {
        headerSection.classList.remove("header-scrolled");
        document.body.classList.remove(this.stickyClasses.pinned);
      }

      this.currentScrollTop = scrollTop;
    });
  }

  _closeMenuDisclosure() {
    this.disclosures =
      this.disclosures || this.header.querySelectorAll("header-menu");
    this.disclosures.forEach((disclosure) => disclosure.close());

    this.siteNav = this.siteNav || this.header.querySelector("site-nav");
    this.siteNav && this.siteNav.closeMegaDropdowns();
  }
}
customElements.define("sticky-header", StickyHeader, { extends: "header" });

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = this.closest(".site-header");
    this.classes = {
      itemActive: "f-menu__item-active",
    };
    this.header.timeoutEnter = null;
    this.header.timeoutLeave = null;
  }

  onToggle(evt) {
    const { target } = evt;
    const li = target.closest(".f-site-nav__item");
    const isOpen = this.mainDetailsToggle.open;
    const isMega = li.classList.contains("f-site-nav__item--mega");

    if (!isOpen) {
      clearTimeout(this.header.timeoutEnter);
      li.classList.remove(this.classes.itemActive);
      this.header.timeoutLeave = setTimeout(() => {
        this.header.handleMegaItemDeactive();
      }, 160);
    } else {
      if (isMega) {
        clearTimeout(this.header.timeoutLeave);
        const dropdown = li.querySelector(".f-site-nav__dropdown");
        this.header.handleMegaItemActive(dropdown);
        this.header.timeoutEnter = setTimeout(() => {
          li.classList.add(this.classes.itemActive);
        }, 160);
      } else {
        li.classList.add(this.classes.itemActive);
      }
    }
  }
}
customElements.define("header-menu", HeaderMenu);

class SiteNav extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = this.closest(".site-header");
    this.classes = {
      itemActive: "f-menu__item-active",
    };

    this.megaItems = this.querySelectorAll(".f-site-nav__item--mega");
    this.timeoutEnter = null;
    this.timeoutLeave = null;
    this.isHover =
      this.header &&
      this.header.classList.contains("show-dropdown-menu-on-hover");
    if (this.isHover) {
      this.megaItems &&
        this.megaItems.forEach((megaItem, index) => {
          megaItem.addEventListener("mouseenter", (evt) =>
            this.onMenuItemEnter(evt, index)
          );
          megaItem.addEventListener("mouseleave", (evt) =>
            this.onMenuItemLeave(evt, index)
          );
        });
    }
  }

  onMenuItemEnter(evt) {
    clearTimeout(this.timeoutLeave);

    const { target } = evt;

    /**
     * If hover from prev item to sibling items then need to close prev sub menu immediately.
     */
    if (!target.classList.contains(this.classes.itemActive)) {
      this.megaItems &&
        this.megaItems.forEach((megaItem) => {
          megaItem.classList.remove(this.classes.itemActive);
        });
    }

    const dropdown = target.querySelector(".f-site-nav__dropdown");

    if (dropdown) {
      this.header.handleMegaItemActive(dropdown);

      this.timeoutEnter = setTimeout(() => {
        target.classList.add(this.classes.itemActive);
      });
    }
  }

  onMenuItemLeave(evt) {
    const { target } = evt;

    clearTimeout(this.timeoutEnter);
    
    this.header.handleMegaItemDeactive();

    this.timeoutLeave = setTimeout(() => {
      target.classList.remove(this.classes.itemActive);
    });
  }

  closeMegaDropdowns() {
    clearTimeout(this.timeoutEnter);
    this.megaItems &&
      this.megaItems.forEach((megaItem) => {
        megaItem.classList.remove(this.classes.itemActive);
      });
    this.timeoutLeave = setTimeout(() => {
      this.header.handleMegaItemDeactive();
    });
  }

  disconnectedCallback() {
    this.megaItems &&
      this.megaItems.forEach((megaItem, index) => {
        megaItem.removeEventListener("mouseenter", (evt) =>
          this.onMenuItemEnter(evt, index)
        );
        megaItem.removeEventListener("mouseleave", (evt) =>
          this.onMenuItemLeave(evt, index)
        );
      });
  }
}
customElements.define("site-nav", SiteNav);

class HeaderAccount extends HTMLElement {
  async connectedCallback() {
    const account = this.querySelector('shopify-account');

    if (!account) return;

    await customElements.whenDefined('shopify-account');

    this.injectAnimation(account);
    // this.watchDialog(account);
  }

  injectAnimation(account) {
    const shadow = account.shadowRoot;

    if (!shadow || shadow.querySelector('#custom-account-styles')) return;

    const style = document.createElement('style');
    style.id = 'custom-account-styles';

    style.textContent = `
      @keyframes dialogScaleIn {
        0% {
          transform: scale(0.8);
          opacity: 0;
        }
        100% {
          transform: scale(1);
          opacity: 1;
        }
      }
      @keyframes dialogScaleOut {
        0% {
          transform: scale(1);
          opacity: 1;
        }

        100% {
          transform: scale(0.8);
          opacity: 0;
        }
      }
      @keyframes dialogBackdropIn {
        0% {
          opacity: 0;
        }

        100% {
          opacity: 1;
        }
      }
      @keyframes dialogBackdropOut {
        0% {
          opacity: 1;
        }

        100% {
          opacity: 0;
        }
      }
      @media (min-width: 751px) {
        dialog[open] {
          animation: dialogScaleIn 0.6s cubic-bezier(0.7, 0, 0.2, 1) !important;
        }
        dialog.closing {
          animation: dialogScaleOut 0.6s cubic-bezier(0.7, 0, 0.2, 1) !important;
        }
        dialog[open]::backdrop {
          cursor: zoom-out;
          animation: dialogBackdropIn 0.8s cubic-bezier(0.7, 0, 0.2, 1) !important;
        }
        dialog.closing::backdrop {
          animation: dialogBackdropOut 0.8s cubic-bezier(0.7, 0, 0.2, 1) !important;
        }
      }
    `;

    shadow.prepend(style);
  }

  watchDialog(account) {
    const shadow = account.shadowRoot;
    if (!shadow) return;

    const dialog = shadow.querySelector('dialog');
    if (!dialog) return;

    const update = () => {
      document.documentElement.classList.toggle('account-dialog-open', dialog.hasAttribute('open'));
    };

    update();

    new MutationObserver(update).observe(dialog, {
      attributes: true,
      attributeFilter: ['open'],
    });
  }
}

customElements.define('header-account', HeaderAccount);
document.addEventListener('click', (event) => {
  const path = event.composedPath();
  const account = path.find((node) => node instanceof Element && node.matches('shopify-account'));
  const button = path.find((node) => node instanceof Element && node.matches('.account-button'));
  if (!account || !button) return;
  const email = account.shadowRoot?.querySelector('.account__email');
  if (!email?.textContent.trim()) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  window.location.assign('/a/customerhub#account:a:dashboard');
}, true);

;(function kunukuMemberHome() {
  if (window.__kunukuMemberHome) return;
  window.__kunukuMemberHome = true;
  const welcomeDesignPreview = false;
  const joiningDesignPreview = false;
  const HOME = '/a/customerhub#account:a:dashboard';
  const PROFILE = '/a/customerhub#account:a:profile';
  const ORDERS = '/a/customerhub#account:a:orders';
  const REWARDS = '/a/customerhub#account:i:1398114';
  const WARRANTY = '/a/customerhub?crew=warranty#account:a:dashboard';
  const EXCLUSIVES = '/a/customerhub?crew=exclusives#account:a:dashboard';
  function hasCrewAccess(menu) {
    return !!menu?.querySelector('a[href="#account:i:1398114"]:not([data-k-custom])');
  }
  const photo = 'https://kunuku.co/cdn/shop/files/Desktop_Banner.png?v=1786226190&width=2560';
  const esc = (v) => String(v).replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const sheet = `
    :host{--k-green:#012b09;--k-cream:#FCFAF7;--k-line:rgba(139,94,60,.18);--k-ink:#132B17;--k-muted:#595959}
    #accounts-hub{max-width:1240px!important;margin:0 auto!important;padding:32px 24px 64px!important}
    #ae-holder>.widget{display:grid!important;grid-template-columns:210px minmax(0,1fr)!important;gap:40px!important;align-items:start!important}
    #ae-holder .menu,#ae-holder .content{width:auto!important;min-width:0!important;margin:0!important;padding:0!important;max-width:none!important}
    #ae-holder .menu .m-hd,#profile-card{display:none!important}
    #ae-holder .menu:before{content:'ACCOUNT';display:block;color:var(--k-green);font-size:12px;letter-spacing:.15em;padding:10px 14px 24px;font-weight:600}
    #menuItems .inner-menu{display:flex!important;flex-direction:column!important;gap:5px!important}
    #menuItems .inner-menu>a{margin:0!important;padding:14px!important;border-radius:3px!important;border:0!important;box-shadow:none!important;font-size:14px!important;background:transparent!important;color:var(--k-ink)!important;text-decoration:none!important;line-height:1.3!important}
    #menuItems .inner-menu>a.active{background:var(--k-green)!important;color:#fff!important}
    #menuItems .inner-menu>a:hover:not(.active){background:var(--k-cream)!important}
    #menuItems [data-k-role=home]{order:0}#menuItems [data-k-role=orders]{order:1}#menuItems [data-k-role=rewards]{order:2}#menuItems [data-k-role=warranty]{order:3}#menuItems [data-k-role=exclusives]{order:4}#menuItems [data-k-role=profile]{order:5}
    #menuItems .inner-menu>[data-k-role=logout]{display:flex!important;order:6;font-size:12px!important;margin-top:20px!important;color:var(--k-muted)!important;padding-top:20px!important;border-top:1px solid var(--k-line)!important}
    #dashboard.k-replaced{display:none!important}
    .k-home{color:var(--k-ink);font-family:inherit;font-size:15px;line-height:1.65;min-width:0}
    .k-join{margin-top:32px;padding:34px;background:var(--k-green);color:#fff;border-radius:3px}.k-home .k-join p,.k-home .k-join h2,.k-home .k-join h3{color:#fff!important}.k-join>p:not(.k-eyebrow){margin-top:16px;max-width:640px}.k-join-benefits{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:22px;margin-top:28px;padding-top:26px;border-top:1px solid #ffffff55}.k-home .k-join-benefits h3{font-size:18px}.k-join-benefits p{font-size:13px;margin-top:9px}.k-home .k-join-note{font-size:12px;line-height:1.7}.k-preview-note{margin:0 0 18px!important;padding:10px 14px;background:#FCFAF7;color:#595959!important;font-size:12px!important}@media(max-width:560px){.k-join{padding:26px}.k-join-benefits{grid-template-columns:1fr;gap:18px}}
    .k-home *{box-sizing:border-box}.k-home [hidden]{display:none!important}.k-home h1,.k-home h2,.k-home h3,.k-home p{margin:0;text-transform:none;letter-spacing:normal;font-family:inherit;color:inherit}
    .k-home h1{font-size:clamp(32px,3.1vw,46px);font-weight:400;line-height:1.12;letter-spacing:-.035em;max-width:620px}.k-home h2{font-size:27px;line-height:1.2;font-weight:400;letter-spacing:-.02em}.k-home h3{font-size:23px;line-height:1.25;font-weight:400;letter-spacing:-.02em}
    .k-home .k-eyebrow{font-size:11px;letter-spacing:.15em;text-transform:uppercase;font-weight:600;line-height:1.5;margin-bottom:18px}.k-home .k-muted{color:var(--k-muted)}
    .k-hero{background:var(--k-cream);padding:36px;display:grid;grid-template-columns:minmax(0,1fr) 218px;gap:26px;align-items:center;border-radius:3px}.k-hero p:not(.k-eyebrow){margin-top:18px;max-width:470px;color:var(--k-muted)}
    .k-balance{background:var(--k-green);color:#fff;padding:24px;border-radius:3px}.k-balance .k-eyebrow{margin-bottom:12px}.k-balance strong{display:block;font-size:44px;line-height:1.1;font-weight:400;font-variant-numeric:tabular-nums}.k-home .k-balance .k-balance-caption{font-size:12px;color:#fff!important;margin:8px 0 20px}.k-balance a{color:#fff!important;text-decoration:none;border-bottom:1px solid #ffffff75;padding-bottom:5px;display:inline-block;font-size:13px}
    .k-section-head{margin:34px 0 18px;display:flex;justify-content:space-between;gap:20px;align-items:baseline}.k-section-head p{font-size:13px;color:var(--k-muted)}
    .k-cards{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px}.k-card{display:flex;flex-direction:column;min-height:210px;padding:26px;border:1px solid var(--k-line);background:#fff;color:var(--k-ink)!important;text-decoration:none!important;border-radius:3px;transition:background .2s,border-color .2s}.k-card:hover{background:#FCFAF7;border-color:#8B5E3C}.k-card p{color:var(--k-muted);margin:12px 0 24px;max-width:330px;font-size:14px}.k-card .k-link{margin-top:auto;color:var(--k-green);font-size:13px;font-weight:600}.k-link span{margin-left:6px}.k-home a:focus-visible,.k-home button:focus-visible{outline:2px solid var(--k-green);outline-offset:4px}.k-home .k-balance a:focus-visible{outline-color:#fff}
    .k-editorial{display:grid;grid-template-columns:1fr 1fr;margin-top:32px;border:1px solid var(--k-line);border-radius:3px;overflow:hidden;background:#fff}.k-editorial img{width:100%;height:100%;min-height:300px;object-fit:cover;object-position:90% center;display:block}.k-editorial-copy{padding:32px;align-self:center}.k-editorial-copy p:not(.k-eyebrow){color:var(--k-muted);margin:16px 0 22px;font-size:14px}.k-text-link{color:var(--k-green)!important;text-decoration:underline;text-underline-offset:5px;font-size:13px;font-weight:600}
    .k-first{margin-top:32px;padding:32px;background:var(--k-cream);border-radius:3px;display:grid;grid-template-columns:1.2fr 1fr;gap:28px;align-items:center}.k-first p:not(.k-eyebrow){color:var(--k-muted);margin-top:14px;font-size:14px}.k-first-list{border-left:1px solid rgba(139,94,60,.22);padding-left:26px}.k-first-list p{padding:9px 0;border-bottom:1px solid rgba(139,94,60,.18);margin:0!important}.k-first-list a{display:inline-block;margin-top:18px}
    .k-page-hero{padding:40px;background:var(--k-cream);border-radius:3px}.k-page-hero>p:not(.k-eyebrow){max-width:610px;margin-top:22px;color:var(--k-muted)}.k-page-hero .k-term{display:inline-block;margin-top:26px;padding-top:18px;border-top:1px solid rgba(139,94,60,.22);font-size:13px;color:var(--k-green)}
    .k-panel{padding:30px;border:1px solid var(--k-line);border-radius:3px;background:#fff}.k-panel p{color:var(--k-muted);margin:14px 0;font-size:14px}.k-panel .k-eyebrow{color:var(--k-green)}.k-panel button[disabled]{border:1px solid rgba(139,94,60,.22);background:#FCFAF7;color:#595959;padding:12px 18px;border-radius:3px;font:inherit;font-size:13px;cursor:not-allowed;margin:10px 0}.k-status{font-size:12px!important;letter-spacing:.02em}.k-note{padding:22px 0;color:var(--k-muted);font-size:13px;max-width:680px}.k-note a{color:var(--k-green)!important}.k-list{margin-top:24px}.k-list article{padding:26px 0;border-bottom:1px solid var(--k-line);display:grid;grid-template-columns:1fr 1.3fr;gap:28px}.k-list p{color:var(--k-muted);font-size:14px}.k-footer-note{margin-top:38px!important;border-top:1px solid var(--k-line);padding-top:18px;font-size:12px;color:var(--k-muted)!important}
    :host([data-k-external]) #accounts-hub{padding:0!important}:host([data-k-external]) #ae-holder>.widget{display:block!important}:host([data-k-external]) .content{display:none!important}
    @media(max-width:900px){#ae-holder{padding:20px 16px 40px!important;width:100%!important;box-sizing:border-box!important}#ae-holder .menu{max-height:none!important;overflow:visible!important;position:static!important;inset:auto!important;transform:none!important;order:0!important;display:block!important;background:transparent!important;border:0!important;box-shadow:none!important;height:auto!important}#ae-holder .content{order:1!important}#menuItems{display:block!important;width:100%!important;height:auto!important;max-height:none!important;overflow:visible!important}#menuItems .inner-menu{position:static!important;width:100%!important;height:auto!important;max-height:none!important;overflow:visible!important}#menuItems .inner-menu>a{height:auto!important;min-height:40px!important;max-height:none!important;width:auto!important;white-space:normal!important;justify-content:center!important}#ae-holder>.widget{grid-template-columns:1fr!important;gap:24px!important}#accounts-hub{padding:22px 18px 44px!important}#ae-holder .menu:before{padding:0 0 16px}#menuItems .inner-menu{display:grid!important;grid-template-columns:repeat(3,minmax(0,1fr));gap:5px!important}#menuItems .inner-menu>a{font-size:12px!important;padding:11px 8px!important;text-align:center}#menuItems .inner-menu>[data-k-role=logout]{grid-column:1/-1;text-align:right;margin:0!important;padding:10px 4px!important;border:0!important}.k-hero{padding:28px;grid-template-columns:1fr 190px}.k-editorial-copy{padding:24px}}
    #menuItems .k-native-signout{grid-column:1/-1;justify-self:end;background:transparent!important;border:0!important;color:var(--k-muted)!important;font:inherit!important;font-size:12px!important;text-decoration:underline;min-height:44px!important;padding:10px 4px!important;cursor:pointer}#menuItems .k-native-signout:focus-visible{outline:2px solid var(--k-green);outline-offset:3px}
    @media(max-width:560px){#menuItems .inner-menu{grid-template-columns:repeat(2,minmax(0,1fr))}.k-hero{grid-template-columns:1fr;padding:26px;gap:24px}.k-balance{padding:22px}.k-balance strong{font-size:40px}.k-cards{grid-template-columns:1fr;gap:12px}.k-card{min-height:180px;padding:24px}.k-section-head{display:block}.k-section-head p{margin-top:8px}.k-editorial,.k-first{grid-template-columns:1fr}.k-editorial img{height:230px;min-height:0}.k-first{padding:26px;gap:22px}.k-first-list{padding:18px 0 0;border-left:0;border-top:1px solid rgba(139,94,60,.22)}.k-page-hero{padding:28px}.k-panel{padding:24px}.k-list article{grid-template-columns:1fr;gap:12px}.k-home h2{font-size:25px}}

    /* Editorial invitation and member treatment; presentation only. */
    .k-home .k-invitation{margin-top:28px;background:var(--k-green);color:#fff;border-radius:3px;overflow:hidden}
    .k-invitation-main{display:grid;grid-template-columns:1.35fr 1fr;min-height:380px}.k-invitation-copy{padding:40px}.k-home .k-invitation-copy p,.k-home .k-invitation-copy h2{color:#fff!important}.k-home .k-invitation-copy h2{font-size:clamp(34px,3vw,44px);line-height:1.07;letter-spacing:-.035em;font-weight:400;margin-top:24px}.k-home .k-invitation-intro{font-size:16px;line-height:1.6;margin-top:22px}.k-invitation-action{display:flex;align-items:center;flex-wrap:wrap;gap:18px;margin-top:28px}.k-home .k-join-cta{appearance:none;display:inline-flex;justify-content:space-between;align-items:center;gap:30px;padding:17px 22px;background:var(--k-cream);border:1px solid var(--k-cream);border-radius:2px;color:var(--k-green)!important;font:inherit;font-size:12px;letter-spacing:.1em;font-weight:600;line-height:1.3;cursor:default;transition:background .18s,border-color .18s}.k-home .k-join-cta:hover{background:#fff;border-color:#fff}.k-home .k-join-cta:focus-visible{outline:2px solid #fff;outline-offset:5px}.k-home .k-join-cta[data-state=pending]{background:#FFDFB3;border-color:#FFDFB3;cursor:progress}.k-invitation-action em{font-size:13px;font-weight:400;color:#fff}.k-invitation-image{position:relative;overflow:hidden;background:#FFDFB3}.k-invitation-image img{display:block;width:100%;height:100%;position:absolute;inset:0;object-fit:cover;object-position:92% center}.k-invitation-image>span{position:absolute;bottom:22px;left:22px;background:var(--k-cream);color:var(--k-green);padding:12px 16px;font-size:12px;line-height:1.5;letter-spacing:.025em}
    .k-invitation-benefits{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));background:var(--k-cream);padding:26px 24px;color:var(--k-green);gap:0}.k-invitation-benefits>div{padding:0 18px;border-left:1px solid rgba(139,94,60,.22)}.k-invitation-benefits>div:first-child{border:0}.k-invitation-benefits strong{display:block;font-size:23px;line-height:1.25;font-weight:400;letter-spacing:-.02em}.k-invitation-benefits span{display:block;font-size:12px;line-height:1.5;margin-top:5px}.k-home .k-invitation-fineprint{padding:11px 40px;background:var(--k-cream);border-top:1px solid rgba(139,94,60,.18);font-size:11px;color:var(--k-muted)!important}.k-sr-only{position:absolute!important;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}
    .k-home .k-member-hero{background:var(--k-green);color:#fff;grid-template-columns:minmax(0,1fr) 235px;padding:34px 38px 38px;column-gap:36px;row-gap:30px}.k-home .k-member-hero h1,.k-home .k-member-hero p{color:#fff!important}.k-home .k-member-status{grid-column:1/-1;display:flex;justify-content:space-between;gap:14px;align-items:center;border-bottom:1px solid #ffffff55;padding-bottom:21px;font-size:11px;letter-spacing:.13em;line-height:1.5}.k-member-status-detail{font-size:12px;letter-spacing:0}.k-home .k-member-welcome>.k-eyebrow{font-size:10px;letter-spacing:.13em;margin-bottom:14px}.k-home .k-member-welcome>p:not(.k-eyebrow){font-size:14px;line-height:1.7;margin-top:20px}.k-home .k-member-hero .k-balance{border-left:1px solid #ffffff55;border-radius:0;padding:4px 0 4px 30px;background:transparent;align-self:center}.k-home .k-member-hero .k-balance .k-eyebrow{font-size:10px;letter-spacing:.12em;margin:0 0 18px}.k-points-line:has(strong[hidden]){display:none}.k-points-line{display:flex;gap:9px;align-items:baseline;flex-wrap:wrap}.k-home .k-member-hero .k-balance strong{font-size:52px;letter-spacing:-.03em;line-height:1}.k-points-line>span{font-size:12px}.k-home .k-member-hero .k-balance .k-balance-caption{margin:14px 0 24px;font-size:12px;line-height:1.6}.k-home .k-member-home-link{display:inline-block;color:#fff!important;text-decoration:underline;text-underline-offset:6px;font-size:13px;margin-top:22px}.k-home .k-member-home-link:focus-visible{outline-color:#fff}
    .k-crew-cards .k-card{border-top:3px solid #012B09;padding:25px 28px 27px;min-height:240px}.k-crew-cards .k-card:before{display:block;font-size:10px;letter-spacing:.13em;line-height:1.5;color:#8B5E3C;margin-bottom:20px}.k-crew-cards .k-card:nth-child(1):before{content:'YOUR COLLECTION'}.k-crew-cards .k-card:nth-child(2){background:#FCFAF7;border-top-color:#8B5E3C}.k-crew-cards .k-card:nth-child(2):before{content:'POINTS & REWARDS'}.k-crew-cards .k-card:nth-child(3){background:#FCFAF7;border-top-color:#8B5E3C}.k-crew-cards .k-card:nth-child(3):before{content:'CARE FOR YOUR PIECES'}.k-crew-cards .k-card:nth-child(4){background:#FCFAF7;border-top-color:#012B09}.k-crew-cards .k-card:nth-child(4):before{content:'MEMBER EXCLUSIVES'}.k-crew-cards .k-card:hover{border-color:var(--k-green);background:#FCFAF7}.k-crew-cards .k-link{padding-top:18px;border-top:1px solid rgba(139,94,60,.18);display:flex;justify-content:space-between;align-items:center}.k-crew-cards .k-link>span{font-size:19px;font-weight:400}
    @media(max-width:900px){.k-invitation-copy{padding:30px}.k-invitation-benefits{padding:24px 12px}.k-invitation-benefits>div{padding:0 12px}.k-invitation-benefits strong{font-size:21px}.k-home .k-member-hero{padding:28px;grid-template-columns:minmax(0,1fr) 190px;column-gap:24px}.k-home .k-member-hero .k-balance{padding-left:24px}.k-member-status-detail{display:none}#ae-holder .menu>.ar{display:none!important}}
    @media(max-width:560px){.k-invitation-main{grid-template-columns:1fr;min-height:0}.k-invitation-copy{padding:30px 26px}.k-home .k-invitation-copy h2{font-size:38px}.k-invitation-image{height:200px}.k-invitation-image>span{bottom:16px;left:18px}.k-invitation-benefits{grid-template-columns:1fr 1fr;row-gap:22px;padding:24px 14px}.k-invitation-benefits>div:nth-child(3){border-left:0}.k-home .k-invitation-fineprint{padding:12px 26px}.k-home .k-member-hero{grid-template-columns:1fr;padding:26px;gap:25px}.k-home .k-member-status{padding-bottom:18px;font-size:10px;letter-spacing:.1em}.k-home .k-member-hero .k-balance{border-left:0;border-top:1px solid #ffffff55;padding:24px 0 0;width:100%}.k-home .k-member-hero .k-balance strong{font-size:48px}.k-crew-cards .k-card{min-height:225px;padding:24px}.k-crew-cards .k-card:before{margin-bottom:16px}}

    :host,#ae-holder{
      --ae-bg:#FCFAF7!important;--ae-bg-body:#FCFAF7!important;--ae-bg-content:#fff!important;--ae-bg-secondary:#fff!important;
      --ae-bg-highlight:#FFDFB3!important;--ae-bg-muted:#FCFAF7!important;--ae-bg-contrast:#132B17!important;
      --ae-text-primary:#132B17!important;--ae-text-muted:#595959!important;--ae-border-color:rgba(139,94,60,.18)!important;
      --ae-btn-primary-bg:#012B09!important;--ae-btn-primary-text:#fff!important;--ae-btn-secondary-bg:#012B09!important;--ae-btn-secondary-text:#fff!important;
      --ae-button-bg:#012B09!important;--ae-button-icon:#fff!important;--ae-btn-muted:#8B5E3C!important;
      --ae-menu-bg:#fff!important;--ae-menu-text:#132B17!important;--ae-menu-active-bg:#012B09!important;--ae-menu-active-text:#fff!important;
      --ae-success:#012B09!important;--ae-success-bg:#FCFAF7!important;--ae-success-fill:#012B09!important;
      --ae-info-text:#132B17!important;--ae-info-bg:#FCFAF7!important;--ae-info-fill:#FFDFB3!important;
      --ae-caution:#8B5E3C!important;--ae-caution-bg:#FCFAF7!important;--ae-caution-fill:#FFDFB3!important;
    }
    #ae-holder .content{box-sizing:border-box!important;padding-inline:24px!important}
    .k-invitation-benefits{background:#FFDFB3}
    @media(max-width:900px){#ae-holder .content{padding-inline:16px!important}}
  `;
  const rewardSheet = `
    #MainContent.with-widget:has(.k-member-rewards){display:grid!important;grid-template-columns:210px minmax(0,1fr)!important;gap:40px!important;max-width:1192px!important;width:calc(100% - 48px)!important;margin:32px auto 64px!important;padding:0!important;align-items:start}
    #MainContent.with-widget:has(.k-member-rewards)>accounts-hub{width:100%!important;max-width:none!important;padding:0!important;position:static!important;margin:0!important}
    #MainContent.with-widget:has(.k-member-rewards)>.ae-content-wrapper{width:100%!important;max-width:none!important;margin:0!important;padding:0!important;min-width:0}
    #appstleLoyaltyPageV2.k-member-rewards{font-family:var(--font-body-family)!important;color:#132B17;--loyalty-background-primary:#fff;--loyalty-background-secondary:#FCFAF7;--loyalty-border-color:rgba(139,94,60,.18);--loyalty-border-color-30:rgba(139,94,60,.18)}
    .k-member-rewards [data-k-onboarding]{display:none!important}.k-member-rewards section{order:4}.k-member-rewards #appstleLoyaltyPageV2HowItWorks{display:none!important}
    .k-member-rewards .appstleLoyaltyPageV2HeroContainer{min-height:290px!important;border-radius:3px!important;padding:34px!important}
    .k-member-rewards .appstleLoyaltyPageV2HeroInformation:before{content:'REWARDS';display:block;font-size:11px;letter-spacing:.17em;color:#fff;margin-bottom:20px}
    .k-member-rewards .appstleLoyaltyPageV2HeroTitle{font-size:clamp(26px,3vw,36px)!important;font-weight:400!important;letter-spacing:-.02em;line-height:1.2!important}
    .k-member-rewards .appstleLoyaltyPageV2HeroCardItem{background:transparent!important;border:none!important;box-shadow:none!important;padding:10px 20px!important}
    .k-member-rewards .appstleLoyaltyPageV2HeroCardValue{font-size:36px!important;font-weight:400!important;background:none!important;color:#fff!important}
    .k-member-rewards .appstleLoyaltyPageV2HeroCardTitle{font-size:12px!important;font-weight:400!important;color:#fff!important}
    .k-member-rewards .appstleLoyaltyPageV2SectionHeader{text-align:left!important;margin-bottom:20px!important}
    .k-member-rewards .appstleLoyaltyPageV2SectionHeaderTitle{font-size:26px!important;font-weight:400!important;color:#132B17!important;letter-spacing:-.02em}
    .k-member-rewards .appstleLoyaltyPageV2SectionHeaderSubTitle{font-size:14px!important;line-height:1.6!important;color:#595959!important}
    .k-member-rewards section:not(#appstleLoyaltyPageV2HeroSection){background:#fff!important;border-bottom:1px solid rgba(139,94,60,.18)}
    .k-member-rewards section:not(#appstleLoyaltyPageV2HeroSection)>div>div{padding:30px 24px!important}
    .k-member-rewards .appstleLoyaltyPageV2WaysToEarnList,.k-member-rewards .appstleLoyaltyPageV2WaysToRedeemList{justify-content:flex-start!important;gap:16px!important}
    .k-member-rewards .appstleLoyaltyPageV2WaysToEarnListItem,.k-member-rewards .appstleLoyaltyPageV2WaysToRedeemListItem{max-width:calc(50% - 8px)!important;text-align:left!important;min-height:180px!important;border-radius:3px!important;transform:none!important;box-shadow:none!important;padding:24px!important;background:#FCFAF7!important}
    .k-member-rewards .appstleLoyaltyPageV2WaysToEarnListItemImageSection,.k-member-rewards .appstleLoyaltyPageV2WaysToRedeemListItemImageSection{display:none!important}
    .k-member-rewards [role=button].al-bg-loyalty-primary{align-self:flex-start!important;border-radius:3px!important;padding:12px 20px!important}
    .k-member-rewards .appstleLoyaltyPageV2WaysToEarnListItemTitle,.k-member-rewards .appstleLoyaltyPageV2WaysToRedeemListItemTitle{font-size:19px!important;font-weight:500!important}
    .k-member-rewards .appstleLoyaltyPageV2WaysToEarnListItemDescription,.k-member-rewards .appstleLoyaltyPageV2WaysToRedeemListItemDescription{font-size:14px!important;color:#595959!important}
    .k-member-rewards #appstleLoyaltyPageV2HeroSection{order:0}.k-member-rewards #appstleLoyaltyPageV2RewardsSection{order:1}.k-member-rewards #appstleLoyaltyPageV2WaysToRedeem{order:2}.k-member-rewards #appstleLoyaltyPageV2EarnRewardsSection{order:3}.k-member-rewards #appstleLoyaltyPageV2TransactionHistorySection{order:5}.k-member-rewards #appstleLoyaltyPageV2FaqSection{order:6}
    .k-member-rewards .appstleLoyaltyPageV2FaqList{padding:0!important}.k-member-rewards .appstleLoyaltyPageV2FaqListItem{padding:18px 0!important}.k-member-rewards .appstleLoyaltyPageV2FaqListItemTitle{font-size:15px!important}
    @media(max-width:900px){#MainContent.with-widget:has(.k-member-rewards)>accounts-hub{grid-column:1!important;grid-row:1!important}#MainContent.with-widget:has(.k-member-rewards)>.ae-content-wrapper{grid-column:1!important;grid-row:2!important}#MainContent.with-widget:has(.k-member-rewards){grid-template-columns:1fr!important;width:calc(100% - 36px)!important;gap:24px!important}}
    @media(max-width:560px){.k-member-rewards .appstleLoyaltyPageV2WaysToEarnListItem,.k-member-rewards .appstleLoyaltyPageV2WaysToRedeemListItem{max-width:100%!important}.k-member-rewards section>div>div{padding:26px 18px!important}.k-member-rewards .appstleLoyaltyPageV2HeroContainer{padding:28px 18px!important}}
  `;
  const studio = () => `<section class="k-editorial"><img src="${photo}" alt="Handmade Kunuku bracelets, crafted from reclaimed wood" loading="lazy"><div class="k-editorial-copy"><p class="k-eyebrow">From the Kunuku Studio</p><h2>Handmade from<br>reclaimed wood.</h2><p>Learn about our materials and how we make Kunuku pieces by hand in Montana.</p><a class="k-text-link" href="/pages/about-us">Explore our story</a></div></section>`;
  const card = (title, copy, cta, url) => `<a class="k-card" href="${url}"><h3>${title}</h3><p>${copy}</p><span class="k-link">${cta} <span aria-hidden="true">→</span></span></a>`;
  function invitation() {
    return `<section class="k-invitation" aria-labelledby="k-invitation-title"><div class="k-invitation-main"><div class="k-invitation-copy"><p class="k-eyebrow">KUNUKU CREW</p><h2 id="k-invitation-title">JOIN THE<br>KUNUKU CREW</h2><p class="k-invitation-intro">Free membership with points, registered warranty coverage and first access.</p><div class="k-invitation-action"><a class="k-join-cta" href="/pages/kunuku-crew#crew-join">JOIN THE KUNUKU CREW <span aria-hidden="true">→</span></a><em>Free to join.</em></div></div><div class="k-invitation-image"><img src="${photo}" alt="Handmade Kunuku bracelets in warm natural wood tones" loading="lazy"><span>Handmade<br>Kunuku bracelets.</span></div></div><div class="k-invitation-benefits"><div><strong>500</strong><span>welcome points</span></div><div><strong>12-month</strong><span>registered warranty</span></div><div><strong>Rewards</strong><span>earn and redeem points</span></div><div><strong>First access</strong><span>+ Crew exclusives</span></div></div><p class="k-invitation-fineprint">Warranty applies to registered eligible pieces.</p></section>`;
  }
  function customerHome() {
    return `<section class="k-page-hero"><p class="k-eyebrow" data-k-customer-welcome>YOUR ACCOUNT</p><h1>Your account.</h1><p>Manage your orders and personal details here.</p></section>${invitation()}<div class="k-section-head"><h2>Manage your account</h2></div><div class="k-cards">${card('Orders','Track your orders and review past purchases.','View your orders',ORDERS)}${card('Profile','Keep your personal details and saved addresses up to date.','Manage your profile',PROFILE)}</div>${studio()}`;
  }
  function home() {
    return `<section class="k-hero k-member-hero"><div class="k-member-status"><span>KUNUKU CREW <span aria-hidden="true">·</span> <span data-k-membership-tier>MEMBER</span></span><span class="k-member-status-detail">Membership active</span></div><div class="k-member-welcome"><p class="k-eyebrow" data-k-welcome>WELCOME TO KUNUKU CREW</p><h1>${welcomeDesignPreview ? "You’re officially in." : "Your account."}</h1><p>${welcomeDesignPreview ? "Your orders, Rewards and Kunuku Crew benefits are all here." : "Manage your orders, Rewards and Kunuku Crew benefits."}</p>${welcomeDesignPreview ? '<a class="k-member-home-link" href="'+HOME+'">Explore your account <span aria-hidden="true">→</span></a>' : ""}</div><aside class="k-balance" aria-label="Rewards"><p class="k-eyebrow">REWARDS</p><p class="k-balance-caption">Earn points. Redeem them for Kunuku Cash.</p><a href="${REWARDS}">VIEW REWARDS <span aria-hidden="true">→</span></a></aside></section><div class="k-section-head"><h2>Make yourself at home.</h2><p>Your Kunuku Crew benefits.</p></div><div class="k-cards k-crew-cards">${card('Orders','Track your orders and review past purchases.','View your orders',ORDERS)}${card('Rewards','Your points, Kunuku Cash and ways to earn.','Explore your rewards',REWARDS)}${card('Warranty','Learn about the 12-month warranty on registered eligible pieces.','Visit Warranty',WARRANTY)}${card('Crew Exclusives','Find early access, member offers and studio updates as they become available.','View Crew Exclusives',EXCLUSIVES)}</div><section class="k-first"><div><p class="k-eyebrow">Crew First Access</p><h2>First access for<br>Kunuku Crew.</h2><p>New releases and member-only offers will appear here as they become available.</p></div><div class="k-first-list"><p>New pieces &amp; limited colors</p><p>Early access &amp; Crew-only offers</p><p>Stories from behind the scenes</p><a class="k-text-link" href="${EXCLUSIVES}">View Crew Exclusives</a></div></section>${studio()}<p class="k-footer-note">Handmade in Montana.</p>`;
  }
  function warranty() {
    return `<section class="k-page-hero"><p class="k-eyebrow">Kunuku Crew · Warranty</p><h1>Kunuku Crew<br>Warranty</h1><p>Kunuku Crew members receive a 12-month warranty on registered eligible pieces. Registration is coming soon to Account.</p><span class="k-term">12-month member warranty · Registered eligible pieces</span></section><div class="k-section-head"><h2>Piece registration</h2><p>Registration is coming soon.</p></div><div class="k-cards"><section class="k-panel"><p class="k-eyebrow">Register a Piece</p><h3>Register an eligible piece.</h3><p>Piece registration is not available yet. When it opens, you’ll be able to add eligible Kunuku pieces here.</p><button type="button" disabled>Register a Piece</button><p class="k-status">Registration opens here soon.</p></section><section class="k-panel"><p class="k-eyebrow">Your Registered Pieces</p><h3>Your registered pieces.</h3><p>This space is reserved for your registered pieces and their coverage details once registration becomes available.</p><p class="k-status">Your registered pieces will appear here.</p></section></div><p class="k-note">Eligibility and registration details will be shared here before registration opens. Need help with a piece you already own? <a href="/pages/contact">Get in touch with Kunuku.</a></p>${studio()}`;
  }
  function exclusives() {
    return `<section class="k-page-hero"><p class="k-eyebrow">Kunuku Crew · Member</p><h1>KUNUKU CREW<br>EXCLUSIVES</h1><p>Find early access, limited releases, member-only offers and content from the Kunuku studio here.</p><span class="k-term">No releases or offers available yet</span></section><div class="k-section-head"><h2>What you'll find here</h2></div><p class="k-muted">New releases, offers and studio content will be added as they become available.</p><div class="k-list"><article><h3>Early access &amp; limited releases</h3><p>Preview new designs and shop selected releases before they open to everyone.</p></article><article><h3>Member-only offers</h3><p>Offers for Kunuku Crew members, with eligibility and terms listed alongside each offer.</p></article><article><h3>From the studio</h3><p>Videos, articles and updates about our materials, process and upcoming work.</p></article></div>`;
  }
  function text(el, value) { if (el && el.textContent !== value) el.textContent = value; }
  const watched=new WeakSet();
  function attach(host) {
    const root=host.shadowRoot;
    if (!root?.querySelector('#menuItems') || watched.has(root)) return;
    watched.add(root);
    const observer=new MutationObserver(schedule);
    let pending=false;
    function schedule() {if(!pending){pending=true;requestAnimationFrame(()=>{pending=false;update();});}}
    function update() {
      observer.disconnect();
      try {
        const holder=root.querySelector('#ae-holder');
        if(holder?.getAttribute('isloggedin')!=='true'){root.getElementById('kunuku-member-content')?.remove();return;}
        if(!root.getElementById('kunuku-member-styles')){const s=document.createElement('style');s.id='kunuku-member-styles';s.textContent=sheet;root.append(s);}
        const external=location.pathname==='/pages/kunuku-crew' && new URLSearchParams(location.search).get('view')==='crew-rewards';
        host.toggleAttribute('data-k-external',external);
        const menu=root.querySelector('#menuItems .inner-menu');
        if(!menu)return;
        const crewMember=hasCrewAccess(menu);
        const query=new URLSearchParams(location.search).get('crew');
        const section=crewMember && ['warranty','exclusives'].includes(query)?query:'home';
        host.toggleAttribute('data-k-customer-design',!crewMember);
        const dashboard=location.hash.includes(':dashboard')?root.querySelector('#dashboard[isloggedin=true]'):null;
        const activeSection=dashboard?section:null;
        for(const a of menu.querySelectorAll('a:not([data-k-custom])')){
          const href=a.getAttribute('href')||'';
          const role=href.includes('crew=warranty')?'warranty':href.includes('crew=exclusives')?'exclusives':href.includes(':dashboard')?'home':href.includes(':orders')?'orders':href.includes(':profile')?'profile':href.includes(':logout')?'logout':href.includes('#account:i:1398114')?'rewards':null;
          if(role)a.dataset.kRole=role;
          if(role==='home')text(a.querySelector('span'),'Account');
          if(role==='rewards')text(a.querySelector('span'),'Rewards');
          if(role){const active=role==='home'?!!dashboard&&section==='home':role==='rewards'?(external||location.hash.includes('#account:i:1398114')):role==='orders'?location.hash.includes(':orders'):role==='profile'?location.hash.includes(':profile'):role==='warranty'||role==='exclusives'?activeSection===role:false;a.classList.toggle('active',active);a.setAttribute('aria-selected',String(active));}
        }
        if (!menu.querySelector('[data-k-role="logout"]')) {
          const signOut = Array.from(root.querySelectorAll('button')).find(button => button.textContent.trim() === 'Sign Out');
          if (signOut) { signOut.dataset.kRole = 'logout'; signOut.classList.add('k-native-signout'); menu.append(signOut); }
        }
        const ordered=['home','orders','rewards','warranty','exclusives','profile','logout'].map(role=>menu.querySelector('[data-k-role='+role+']')).filter(Boolean);
        if(ordered.some((a,i)=>menu.children[i]!==a))ordered.forEach(a=>menu.append(a));
        let page=root.getElementById('kunuku-member-content');
        if(dashboard){
          dashboard.classList.add('k-replaced');
          if(!page){page=document.createElement('div');page.id='kunuku-member-content';page.className='k-home';dashboard.parentElement.insertBefore(page,dashboard);}
          const renderKey=(crewMember?'crew:':'customer:')+section;
          if(page.dataset.section!==renderKey){page.innerHTML=crewMember?(section==='warranty'?warranty():section==='exclusives'?exclusives():home()):customerHome();page.dataset.section=renderKey;}
          const pointsElement=page.querySelector('[data-k-points]');
          if (crewMember && section==='home' && pointsElement) {
            const source=document.getElementById('kunuku-account-points');
            const value=source ? JSON.parse(source.textContent) : null;
            if (Number.isSafeInteger(value) && value>=0) {
              text(pointsElement,value.toLocaleString('en-US'));
              pointsElement.hidden=false;
            }
          }
          const welcome=root.querySelector('.menu .m-hd .c-hd')?.textContent.replace(/^\s*Welcome\s*/i,'').trim();
          text(page.querySelector('[data-k-customer-welcome]'),welcome?'WELCOME BACK, '+welcome.toUpperCase():'YOUR ACCOUNT');
          text(page.querySelector('[data-k-welcome]'),welcome?'WELCOME TO KUNUKU CREW, '+welcome.toUpperCase():'WELCOME TO KUNUKU CREW');

        }else if(page){page.remove();}
      } finally {observer.observe(root,{childList:true,subtree:true,characterData:true});}
    }
    root.addEventListener('click',(event)=>{
      const a=event.composedPath().find(n=>n instanceof Element&&n.matches('#menuItems a:not([data-k-custom])'));
      if(a && ['warranty','exclusives'].includes(a.dataset.kRole)){event.preventDefault();event.stopImmediatePropagation();if(hasCrewAccess(root.querySelector('#menuItems .inner-menu')))location.assign(a.dataset.kRole==='warranty'?WARRANTY:EXCLUSIVES);return;}
      if(a && new URLSearchParams(location.search).has('crew')){const u=new URL(location.href);u.searchParams.delete('crew');history.replaceState(null,'',u.pathname+u.search+u.hash);schedule();}
    },true);
    window.addEventListener('hashchange',schedule);
    window.addEventListener('popstate',schedule);
    document.addEventListener('kunuku:balance',schedule);
    update();
  }
  let queued=false;
  function refresh(){if(queued)return;queued=true;requestAnimationFrame(()=>{queued=false;document.querySelectorAll('accounts-hub').forEach(attach);document.dispatchEvent(new Event('kunuku:balance'));});}
  const docObserver=new MutationObserver(refresh);
  docObserver.observe(document.documentElement,{childList:true,subtree:true,characterData:true});
  let attempts=0;const timer=setInterval(()=>{refresh();if(++attempts>120)clearInterval(timer);},500);
  refresh();
})();
